/**
 * @file detailsStep.component.tsx
 * @path apps/website/src/features/booking/components/detailsStep.component.tsx
 * @description Trip details step with collapsible sections (legacy UI).
 */

import { Passenger } from "@sirbastians/types";
import {
  Button,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from "@sirbastians/ui";
import { cn, formatCurrency } from "@sirbastians/utils";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useBookingStore } from "../booking.store";
import { validateDetailsStep } from "../booking.schema";
import {
  AirplaneFill,
  AlarmFill,
  CarFront,
  ChevronDown,
  Dash,
  EnvelopeAtFill,
  PersonFill,
  PersonFillAdd,
  PhoneFill,
  Plus,
} from "react-bootstrap-icons";

// ── Counter ─────────────────────────────────────────────────────────────────

const Counter = ({
  label,
  sublabel,
  count,
  onIncrement,
  onDecrement,
  min = 0,
}: {
  label: string;
  sublabel?: string;
  count: number;
  onIncrement: () => void;
  onDecrement: () => void;
  min?: number;
}) => (
  <div className="flex items-center justify-between py-3 border-b last:border-0">
    <div>
      {label && <p className="font-medium text-sm">{label}</p>}
      {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
    </div>
    <div className="flex items-center gap-3">
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8 rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          onDecrement();
        }}
        disabled={count <= min}
      >
        <Dash className="h-3 w-3" />
      </Button>
      <span className="w-6 text-center font-semibold text-sm">{count}</span>
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8 rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          onIncrement();
        }}
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  </div>
);

// ── CollapsibleSection ──────────────────────────────────────────────────────

const CollapsibleSection = ({
  title,
  icon: Icon,
  children,
  hasError = false,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  hasError?: boolean;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={cn(
        "border rounded-xl bg-card transition-all duration-200 overflow-hidden",
        hasError
          ? "border-destructive ring-1 ring-destructive/50"
          : "border-border hover:border-accent/50",
        isOpen && !hasError && "ring-1 ring-accent/20 border-accent/50",
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={cn(
          "w-full flex items-center justify-between p-4 text-left transition-colors",
          isOpen ? "bg-muted/30" : "hover:bg-muted/30",
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "p-2 rounded-full bg-accent/10 text-accent",
              hasError && "bg-destructive/10 text-destructive",
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
          <span className={cn("font-semibold", hasError && "text-destructive")}>
            {title}
          </span>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-300",
            isOpen && "rotate-180",
          )}
        />
      </button>

      <div
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="p-4 pt-2 border-t border-border/50">{children}</div>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

export const DetailsStep = () => {
  const store = useBookingStore();
  const {
    selectedService,
    passengers,
    adultsCount,
    childrenCount,
    infantsCount,
    babySeats,
    trailerNeeded,
    flightNumber,
    duration,
    selectedVehicles,
    showValidation,
  } = store;

  const validation = showValidation ? validateDetailsStep(store) : null;
  const fieldError = (elementId: string) =>
    validation?.fieldErrors.find((e) => e.elementId === elementId)?.message;

  // Passengers are now auto-initialized in setService — no mount init needed

  const primaryPassenger = passengers.find((p) => p.isPrimary);
  const isAirport = selectedService?.slug === "airport-transfers";
  const isPrivateHire = selectedService?.slug === "private-vehicle-hire";

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="text-left space-y-2 mb-6">
        <h2 className="text-2xl font-bold">Trip Details</h2>
        <p className="text-muted-foreground">
          Tell us who is traveling and what you need.
        </p>
      </div>

      {/* ── Passengers ───────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Who's Traveling?"
        icon={PersonFillAdd}
        defaultOpen={true}
        hasError={!!fieldError("passengers")}
      >
        <div className="space-y-2" data-field-id="passengers">
          <Counter
            label="Adults"
            count={adultsCount}
            onDecrement={() =>
              store.updatePassengerCount("adult", adultsCount - 1)
            }
            onIncrement={() =>
              store.updatePassengerCount("adult", adultsCount + 1)
            }
            min={1}
          />
          <Counter
            label="Children"
            sublabel="Ages 2-12"
            count={childrenCount}
            onDecrement={() =>
              store.updatePassengerCount("child", childrenCount - 1)
            }
            onIncrement={() =>
              store.updatePassengerCount("child", childrenCount + 1)
            }
          />
          <Counter
            label="Infants"
            sublabel="Under 2 (Lap)"
            count={infantsCount}
            onDecrement={() =>
              store.updatePassengerCount("infant", infantsCount - 1)
            }
            onIncrement={() =>
              store.updatePassengerCount("infant", infantsCount + 1)
            }
          />
          <Counter
            label="Baby Seats"
            sublabel="Takes 1 Seat (+R350)"
            count={babySeats}
            onDecrement={() => store.updateBabySeatCount(babySeats - 1)}
            onIncrement={() => store.updateBabySeatCount(babySeats + 1)}
          />
        </div>
        {fieldError("passengers") && (
          <p className="text-sm text-destructive mt-2">
            {fieldError("passengers")}
          </p>
        )}
      </CollapsibleSection>

      {/* ── Contact Details ───────────────────────────────────────────── */}
      <CollapsibleSection
        title="Contact Details"
        icon={EnvelopeAtFill}
        defaultOpen={true}
        hasError={
          !!fieldError("primaryFirstName") ||
          !!fieldError("primaryEmail") ||
          !!fieldError("primaryPhone")
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2" data-field-id="primaryFirstName">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              value={primaryPassenger?.firstName || ""}
              onChange={(e) =>
                store.updatePrimaryContact("firstName", e.target.value)
              }
              placeholder="e.g. John"
              className={cn(
                fieldError("primaryFirstName") && "border-destructive",
              )}
            />
            {fieldError("primaryFirstName") && (
              <p className="text-xs text-destructive">
                {fieldError("primaryFirstName")}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              value={primaryPassenger?.lastName || ""}
              onChange={(e) =>
                store.updatePrimaryContact("lastName", e.target.value)
              }
              placeholder="e.g. Doe"
            />
          </div>
          <div className="space-y-2" data-field-id="primaryEmail">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <EnvelopeAtFill className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={primaryPassenger?.email || ""}
                onChange={(e) =>
                  store.updatePrimaryContact("email", e.target.value)
                }
                placeholder="john@example.com"
                className={cn(
                  "pl-9",
                  fieldError("primaryEmail") && "border-destructive",
                )}
              />
            </div>
            {fieldError("primaryEmail") && (
              <p className="text-xs text-destructive">
                {fieldError("primaryEmail")}
              </p>
            )}
          </div>
          <div className="space-y-2" data-field-id="primaryPhone">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="relative">
              <PhoneFill className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                value={primaryPassenger?.phone || ""}
                onChange={(e) =>
                  store.updatePrimaryContact("phone", e.target.value)
                }
                placeholder="+27 12 345 6789"
                className={cn(
                  "pl-9",
                  fieldError("primaryPhone") && "border-destructive",
                )}
              />
            </div>
            {fieldError("primaryPhone") && (
              <p className="text-xs text-destructive">
                {fieldError("primaryPhone")}
              </p>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* ── Logistics (Airport / Private Hire) ─────────────────────── */}
      {(isAirport || isPrivateHire) && (
        <CollapsibleSection
          title="Logistics"
          icon={isAirport ? AirplaneFill : AlarmFill}
          hasError={
            !!fieldError("flightNumber") || !!fieldError("duration-selector")
          }
          defaultOpen={false}
        >
          {isAirport && (
            <div className="space-y-2" data-field-id="flightNumber">
              <Label className="flex items-center gap-2">Flight Number</Label>
              <Input
                placeholder="e.g. BA432"
                value={flightNumber || ""}
                onChange={(e) => store.setFlightNumber(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                We track this to ensure your driver is there when you land.
              </p>
              {fieldError("flightNumber") && (
                <p className="text-xs text-destructive">
                  {fieldError("flightNumber")}
                </p>
              )}
            </div>
          )}

          {isPrivateHire && (
            <div className="space-y-3" data-field-id="duration-selector">
              <Label>Select Duration</Label>
              <RadioGroup
                value={duration || ""}
                onValueChange={(v: string) =>
                  store.setDuration(v as "half-day" | "full-day")
                }
                className="grid grid-cols-2 gap-4"
              >
                <div
                  className={cn(
                    "border rounded-lg p-3 cursor-pointer transition-all hover:border-accent",
                    duration === "half-day" &&
                      "border-accent bg-accent/5 ring-1 ring-accent",
                  )}
                >
                  <RadioGroupItem
                    value="half-day"
                    id="hd"
                    className="sr-only"
                  />
                  <Label
                    htmlFor="hd"
                    className="cursor-pointer block text-center"
                  >
                    <div className="font-semibold">Half Day</div>
                    <div className="text-xs text-muted-foreground">4 Hours</div>
                  </Label>
                </div>
                <div
                  className={cn(
                    "border rounded-lg p-3 cursor-pointer transition-all hover:border-accent",
                    duration === "full-day" &&
                      "border-accent bg-accent/5 ring-1 ring-accent",
                  )}
                >
                  <RadioGroupItem
                    value="full-day"
                    id="fd"
                    className="sr-only"
                  />
                  <Label
                    htmlFor="fd"
                    className="cursor-pointer block text-center"
                  >
                    <div className="font-semibold">Full Day</div>
                    <div className="text-xs text-muted-foreground">8 Hours</div>
                  </Label>
                </div>
              </RadioGroup>
              {fieldError("duration-selector") && (
                <p className="text-xs text-destructive">
                  {fieldError("duration-selector")}
                </p>
              )}
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* ── Vehicles ─────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Vehicles"
        icon={CarFront}
        hasError={!!fieldError("vehicle-selector")}
        defaultOpen={false}
      >
        <div data-field-id="vehicle-selector" className="space-y-4">
          <div className="space-y-2">
            {selectedService?.standardVehicle && (
              <div className="flex items-center justify-between p-3 border rounded-lg bg-card">
                <div>
                  <p className="font-medium text-sm">
                    {selectedService.standardVehicle.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Max {selectedService.standardVehicle.maxPassengers} Pax
                  </p>
                </div>
                <Counter
                  label=""
                  count={
                    selectedVehicles.find(
                      (v) => v.id === selectedService.standardVehicle!.id,
                    )?.count || 0
                  }
                  onDecrement={() =>
                    store.handleVehicleChange(
                      selectedService.standardVehicle!.id,
                      "standard",
                      -1,
                    )
                  }
                  onIncrement={() =>
                    store.handleVehicleChange(
                      selectedService.standardVehicle!.id,
                      "standard",
                      1,
                    )
                  }
                />
              </div>
            )}

            {selectedService?.vipOptions?.map(
              (vip: {
                id: string;
                label: string;
                maxPassengers: number;
                price: number;
              }) => (
                <div
                  key={vip.id}
                  className="flex items-center justify-between p-3 border rounded-lg bg-card"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{vip.label}</p>
                      <span className="bg-yellow-100 text-yellow-800 text-[9px] px-1.5 py-0.5 rounded-full uppercase font-bold tracking-wide">
                        VIP
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Max {vip.maxPassengers} Pax ·{" "}
                      <span className="text-accent font-semibold">
                        +{formatCurrency(vip.price)}
                      </span>
                    </p>
                  </div>
                  <Counter
                    label=""
                    count={
                      selectedVehicles.find((v) => v.id === vip.id)?.count || 0
                    }
                    onDecrement={() =>
                      store.handleVehicleChange(vip.id, "vip", -1)
                    }
                    onIncrement={() =>
                      store.handleVehicleChange(vip.id, "vip", 1)
                    }
                  />
                </div>
              ),
            )}
          </div>

          {fieldError("vehicle-selector") && (
            <p className="text-xs text-destructive">
              {fieldError("vehicle-selector")}
            </p>
          )}

          {selectedService?.exclusions?.some((e) =>
            e.toLowerCase().includes("trailer"),
          ) && (
            <div className="flex items-center space-x-3 pt-2 border-t">
              <Checkbox
                id="trailer"
                checked={trailerNeeded}
                onCheckedChange={(checked: boolean) =>
                  store.setTrailerNeeded(checked)
                }
              />
              <div className="grid gap-0.5 leading-none">
                <Label
                  htmlFor="trailer"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Add Luggage Trailer (+R450)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Recommended for {adultsCount} suitcases.
                </p>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};
